import { Chip, Box, Button, Typography } from "@mui/material";
import {
  CheckCircle,
  HourglassEmpty,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import moment from "moment";
import { Vocabulary } from "../../Utils/Vocabulary";
import { ICalendarEvent } from "../Calendar/CalendarUtils";
import { HourStatusEnum } from "../../Utils/Enums";
import styles from "./DrivingSessionScheduling.module.css";

/**
 * Renders a chip representing the status of a driving session.
 * @param {boolean} isAvailable - Whether the driving session is available.
 * @param {boolean} isBooked - Whether the driving session is booked.
 * @param {boolean} isApproved - Whether the driving session is approved.
 * @returns {JSX.Element | null} - A chip representing the status of a driving session, or null if no status is applicable.
 */
export const renderStatusChip = (
  isAvailable: boolean,
  isBooked: boolean,
  isApproved: boolean
) => {
  if (isAvailable) {
    return (
      <Chip
        icon={<CheckCircle />}
        label={Vocabulary.available}
        size="small"
        className={`${styles.statusChip} ${styles.availableChip}`}
      />
    );
  }
  if (isApproved) {
    return (
      <Chip
        icon={<CheckCircle />}
        label={Vocabulary.approved}
        size="small"
        className={`${styles.statusChip} ${styles.approvedChip}`}
      />
    );
  }
  if (isBooked) {
    return (
      <Chip
        icon={<HourglassEmpty />}
        label={Vocabulary.waitingInstructorApproval}
        size="small"
        className={`${styles.statusChip} ${styles.bookedChip}`}
      />
    );
  }
  return null;
};

/**
 * Transforms an array of hours into an array of ICalendarEvents.
 * The event title depends on the hour status.
 * If the hour is available, the title is 'available'.
 * If the hour is pending, the title is 'waitingInstructorApproval'.
 * If the hour is booked, the title is 'booked'.
 *
 * @param {any[]} hours - The array of hours to transform.
 * @returns {ICalendarEvent[]} - The array of transformed ICalendarEvents.
 */
export const transformHoursToEvents = (hours: any[]): ICalendarEvent[] => {
  const clientId = localStorage.getItem("clientId");

  return hours.map((hour) => {
    const startDate = moment(hour.startDate);
    const instructor = hour.instructorId?.user_id;
    const isApproved = hour.status === HourStatusEnum.APPROVED;
    const isPending = hour.status === HourStatusEnum.PENDING;
    const isOwnBooking = hour.clientId === clientId;

    return {
      id: hour._id || hour.id,
      date: startDate.format("YYYY-MM-DDTHH:mm"),
      title:
        hour.status === HourStatusEnum.AVAILABLE
          ? Vocabulary.available
          : hour.status === HourStatusEnum.PENDING
          ? Vocabulary.waitingInstructorApproval
          : Vocabulary.booked,
      available: hour.status === HourStatusEnum.AVAILABLE,
      booked: isPending,
      approved: isApproved,
      instructorName: instructor?.firstName + " " + instructor?.lastName,
      startDate: hour.startDate,
      endDate: hour.endDate,
      isOwnBooking,
      status: hour.status,
      clientId: hour.clientId || null,
      orderId: hour.orderId || null,
    };
  });
};

/**
 * Generates an array of time slots in the format "HH:MM" from 08:00 to 20:00.
 * @returns {string[]} An array of time slots.
 */
export const generateTimeSlots = (): string[] => {
  const slots: string[] = [];
  for (let hour = 8; hour <= 20; hour++) {
    slots.push(`${hour.toString().padStart(2, "0")}:00`);
  }
  return slots;
};

/**
 * Formats a week range string based on the given start date.
 * @param {moment.Moment} currentWeekStart - start date of the week
 * @returns {string} a string in the format "DD MMM - DD MMM YYYY"
 */
export const formatWeekRange = (currentWeekStart: moment.Moment): string => {
  const start = currentWeekStart.format("DD MMM");
  const end = currentWeekStart.clone().add(6, "days").format("DD MMM YYYY");
  return `${start} - ${end}`;
};

/**
 * Formats a time display string based on the given start and end dates
 * @param {string|Date} startDate - start date in the format "YYYY-MM-DD" or a Date object
 * @param {string|Date} endDate - end date in the format "YYYY-MM-DD" or a Date object
 * @returns {string} formatted time display string in the format "HH:mm - HH:mm" if both start and end dates are valid, empty string otherwise
 */
export const formatTimeDisplay = (
  startDate: string | Date,
  endDate: string | Date
): string => {
  const startTime = moment(startDate).format("HH:mm");
  const endTime = moment(endDate).format("HH:mm");
  return startTime && endTime ? `${startTime} - ${endTime}` : "";
};

/**
 * Checks if the given client has a booking on the target date
 * @param {any[]} hours - array of hours
 * @param {string} targetDate - date to check in the format "YYYY-MM-DD"
 * @param {string} clientId - client id to check
 * @returns {boolean} true if the client has a booking on the target date, false otherwise
 */
export const hasBookingOnDate = (
  hours: any[],
  targetDate: string,
  clientId: string
): boolean => {
  return hours.some((hour) => {
    const hourDate = moment(hour.startDate).format("YYYY-MM-DD");
    const isClientBooked =
      hour.clientId === clientId &&
      (hour.status === HourStatusEnum.PENDING ||
        hour.status === HourStatusEnum.APPROVED);
    return hourDate === targetDate && isClientBooked;
  });
};

/**
 * Navigates to the previous week
 * @param {moment.Moment} currentWeekStart - current week start date
 * @returns {moment.Moment} the previous week start date
 */
export const navigateToPreviousWeek = (
  currentWeekStart: moment.Moment
): moment.Moment => {
  return currentWeekStart.clone().subtract(1, "week");
};

/**
 * Navigates to the next week
 * @param {moment.Moment} currentWeekStart - current week start date
 * @returns {moment.Moment} the next week start date
 */
export const navigateToNextWeek = (
  currentWeekStart: moment.Moment
): moment.Moment => {
  return currentWeekStart.clone().add(1, "week");
};

/**
 * Navigates to the current week
 * @returns {moment.Moment} the current week start date
 */
export const navigateToCurrentWeek = (): moment.Moment => {
  return moment().startOf("isoWeek");
};

/**
 * Week Navigation Header Component
 * Displays week range with navigation controls
 */
interface WeekNavigationHeaderProps {
  currentWeekStart: moment.Moment;
  setCurrentWeekStart: (date: moment.Moment) => void;
}

/**
 * Week Navigation Header Component
 * Displays week range with navigation controls
 * @param {moment.Moment} currentWeekStart - current week start date
 * @param {(date: moment.Moment) => void} setCurrentWeekStart - setter function for current week start
 */
export const WeekNavigationHeader = ({
  currentWeekStart,
  setCurrentWeekStart,
}: WeekNavigationHeaderProps) => {
  return (
    <Box className={styles.headerSection}>
      <Box className={styles.weekNavigation}>
        <Button
          onClick={() =>
            setCurrentWeekStart(navigateToPreviousWeek(currentWeekStart))
          }
          startIcon={<NavigateBefore />}
          variant="outlined"
          className={styles.navButton}
        >
          {Vocabulary.previous}
        </Button>
        <Box className={styles.weekRange}>
          <Typography variant="h6">
            {formatWeekRange(currentWeekStart)}
          </Typography>
          <Button
            onClick={() => setCurrentWeekStart(navigateToCurrentWeek())}
            variant="text"
            size="small"
            className={styles.todayButton}
          >
            Săptămâna curentă
          </Button>
        </Box>
        <Button
          onClick={() =>
            setCurrentWeekStart(navigateToNextWeek(currentWeekStart))
          }
          endIcon={<NavigateNext />}
          variant="outlined"
          className={styles.navButton}
        >
          {Vocabulary.next}
        </Button>
      </Box>
    </Box>
  );
};
